/**
 * ============================================================
 *  BASTION ANTI-CLONAGE — empreinte d'intégrité côté client
 * ============================================================
 * Deux protections complémentaires :
 *   1. Anti-framing : si le site est encapsulé dans une iframe
 *      étrangère (technique de clonage la plus courante), on
 *      force la sortie vers le domaine légitime.
 *   2. Empreinte SHA-256 de la structure du DOM envoyée au
 *      serveur : toute divergence (page copiée puis modifiée,
 *      proxy hostile, injection) est journalisée dans le Bastion.
 *
 * Aucune donnée personnelle n'est transmise : uniquement un hash
 * de la structure HTML (balises), le chemin et l'origine.
 *
 * ⚠️ ACTIF EN PRODUCTION UNIQUEMENT (03/07/2026).
 * En preview/développement, l'application est servie dans une iframe cross-origin :
 * toute écriture sur `window.top.location` y lève « The operation is insecure ».
 * Le garde ci-dessous (`EN_PRODUCTION`) neutralise donc définitivement le module hors
 * production — ne pas le retirer, c'est la correction permanente de cette erreur.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
/** `NODE_ENV` vaut "production" uniquement dans le build servi sur le site live. */
const EN_PRODUCTION = process.env.NODE_ENV === "production";

/** Signature structurelle : seules les balises comptent (le contenu dynamique est ignoré). */
const structureDom = () => {
  const balises = Array.from(document.querySelectorAll("body *"))
    .slice(0, 400)
    .map((n) => n.tagName.toLowerCase())
    .join(">");
  return `${window.location.pathname}|${balises}`;
};

const sha256 = async (texte) => {
  const octets = new TextEncoder().encode(texte);
  const digest = await window.crypto.subtle.digest("SHA-256", octets);
  return Array.from(new Uint8Array(digest))
    .map((o) => o.toString(16).padStart(2, "0"))
    .join("");
};

/** Sortie forcée d'une iframe non autorisée (complément de X-Frame-Options). */
const protegerContreFraming = () => {
  if (!EN_PRODUCTION) return;
  try {
    if (window.top === window.self) return;
    if (window.top.location.origin !== window.location.origin) {
      window.top.location = window.location.href;
    }
  } catch {
    // Accès cross-origin refusé ⇒ encapsulation suspecte. L'écriture sur window.top
    // peut elle-même être bloquée (« The operation is insecure ») : on l'isole.
    try {
      window.top.location = window.location.href;
    } catch {
      /* navigateur verrouillé : on laisse la page en place sans casser l'application */
    }
  }
};

export const initAntiClonage = async () => {
  if (!EN_PRODUCTION) return;   // preview / développement : module entièrement désactivé
  protegerContreFraming();
  if (!window.crypto?.subtle) return; // contexte non sécurisé : rien à faire
  try {
    const empreinte = await sha256(structureDom());
    await fetch(`${API}/securite/empreinte`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empreinte, page: window.location.pathname }),
      keepalive: true,
    });
  } catch {
    // La télémétrie de sécurité ne doit jamais bloquer l'expérience utilisateur
  }
};
